import React, { useState, useEffect } from "react";
import { authenticatedFetch } from "@/libs/api";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Tabs,
  message,
  Dropdown,
  Spin,
  theme,
} from "antd";
import {
  DollarOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Sharp, tech-style input
const inputStyle = { borderRadius: 2 };

export default function HistoricalPayments() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, "month"),
    dayjs(),
  ]);

  const [monthlyData, setMonthlyData] = useState([]);
  const [partnerPayments, setPartnerPayments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch company ledger (partner payments) and customer ledger (client payments)
        const [compRes, custRes] = await Promise.all([
          authenticatedFetch("/api/settlements/ledger/company?limit=100"),
          authenticatedFetch("/api/settlements/ledger/customer?limit=100"),
        ]);

        let compData = [];
        let custData = [];

        if (compRes.ok) {
          const compJson = await compRes.json();
          if (compJson.r === 0 && compJson.data) {
            compData = compJson.data;
            const entries = compData.map((e, i) => ({
              id: `PPAY-${e.date}-${i}`,
              partnerId: e.company_id || "",
              partnerName: e.company_id || "Partner",
              paymentDate: e.date,
              periodStart: e.date,
              periodEnd: e.date,
              systemCount: 0,
              payableAmount: Number(e.debit) || 0,
              paidAmount: Number(e.debit) || 0,
              status: "paid",
            }));
            setPartnerPayments(entries);
          }
        }

        if (custRes.ok) {
          const custJson = await custRes.json();
          if (custJson.r === 0 && custJson.data) {
            custData = custJson.data;
            const entries = custData.map((e, i) => ({
              id: `CPAY-${e.date}-${i}`,
              clientId: e.customer_id || "",
              clientName: e.customer_id || "Client",
              email: "",
              paymentDate: e.date,
              periodStart: e.date,
              periodEnd: e.date,
              systemSn: "",
              payableAmount: Number(e.debit) || 0,
              paidAmount: Number(e.debit) || 0,
              status: "paid",
            }));
            setClientPayments(entries);
          }
        }

        // Build monthly chart data from both ledger sources
        const monthMap = {};
        compData.forEach((e) => {
          const month = dayjs(e.date).format("MMM YYYY");
          if (!monthMap[month]) monthMap[month] = { month, partnerPaid: 0, clientPaid: 0 };
          monthMap[month].partnerPaid += Number(e.debit) || 0;
        });
        custData.forEach((e) => {
          const month = dayjs(e.date).format("MMM YYYY");
          if (!monthMap[month]) monthMap[month] = { month, partnerPaid: 0, clientPaid: 0 };
          monthMap[month].clientPaid += Number(e.debit) || 0;
        });
        setMonthlyData(
          Object.values(monthMap).sort(
            (a, b) => dayjs(a.month, "MMM YYYY").unix() - dayjs(b.month, "MMM YYYY").unix()
          )
        );
      } catch (error) {
        console.error("Failed to fetch historical payments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate stats
  const totalPartnerPaid = partnerPayments.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalClientPaid = clientPayments.reduce((sum, c) => sum + c.paidAmount, 0);
  const totalPaid = totalPartnerPaid + totalClientPaid;
  const partnerCount = [...new Set(partnerPayments.map((p) => p.partnerId))].length;
  const clientCount = [...new Set(clientPayments.map((c) => c.clientId))].length;

  const exportMenuItems = [
    { key: "excel", label: "Export to Excel", icon: <FileExcelOutlined /> },
    { key: "csv", label: "Export to CSV", icon: <FileExcelOutlined /> },
    { key: "pdf", label: "Export to PDF", icon: <FilePdfOutlined /> },
  ];

  const handleExport = ({ key }) => {
    message.success(`Exporting to ${key.toUpperCase()}...`);
  };

  const partnerColumns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      key: "id",
      render: (id) => <Text strong style={{ fontSize: 12 }}>{id}</Text>,
    },
    {
      title: "Partner",
      dataIndex: "partnerName",
      key: "partnerName",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.partnerId}</Text>
        </Space>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>{record.periodStart} ~ {record.periodEnd}</Text>
      ),
    },
    {
      title: "Systems",
      dataIndex: "systemCount",
      key: "systemCount",
      align: "right",
    },
    {
      title: "Payable",
      dataIndex: "payableAmount",
      key: "payableAmount",
      align: "right",
      render: (amount) => `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      key: "paidAmount",
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: token.colorSuccess }}>
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: () => <Tag color="success">Paid</Tag>,
    },
  ];

  const clientColumns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      key: "id",
      render: (id) => <Text strong style={{ fontSize: 12 }}>{id}</Text>,
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
        </Space>
      ),
    },
    {
      title: "System",
      dataIndex: "systemSn",
      key: "systemSn",
      render: (sn) => <Text code style={{ fontSize: 11 }}>{sn}</Text>,
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>{record.periodStart} ~ {record.periodEnd}</Text>
      ),
    },
    {
      title: "Payable",
      dataIndex: "payableAmount",
      key: "payableAmount",
      align: "right",
      render: (amount) => `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      key: "paidAmount",
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: token.colorSuccess }}>
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: () => <Tag color="success">Paid</Tag>,
    },
  ];

  return (
    <PageLayout active="historicalPayments">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Historical Payments
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              View payment history for partners and end users
            </Text>
          </div>
          <Dropdown menu={{ items: exportMenuItems, onClick: handleExport }}>
            <Button size="large" style={inputStyle}>
              Export <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              picker="month"
              size="large"
              style={{ width: 280, ...inputStyle }}
            />
          </div>
        </Space>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Paid"
              value={totalPaid}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Partner Payments"
              value={totalPartnerPaid}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#f5a623" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Client Payments"
              value={totalClientPaid}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Recipients"
              value={partnerCount + clientCount}
              suffix={`(${partnerCount} partners, ${clientCount} clients)`}
              valueStyle={{ fontSize: 24 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Monthly Chart */}
      <Card
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 12 }}
        title={<Text strong>Monthly Payment Distribution</Text>}
      >
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <RechartsTooltip
                formatter={(value) => [`$${value.toLocaleString()}`, ""]}
              />
              <Legend />
              <Bar dataKey="partnerPaid" name="Partner Payments" fill="#f5a623" radius={[4, 4, 0, 0]} />
              <Bar dataKey="clientPaid" name="Client Payments" fill="#13c2c2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Two-column layout: Partner (left) and Client (right) */}
      <Row gutter={24}>
        {/* Partner Section - Left */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, height: "100%" }}
            title={
              <Space>
                <TeamOutlined style={{ color: "#f5a623" }} />
                <Text strong>Partner Payment History</Text>
              </Space>
            }
          >
            <Table
              columns={partnerColumns}
              dataSource={partnerPayments}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>

        {/* Client Section - Right */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, height: "100%" }}
            title={
              <Space>
                <UserOutlined style={{ color: "#13c2c2" }} />
                <Text strong>Client Payment History</Text>
              </Space>
            }
          >
            <Table
              columns={clientColumns}
              dataSource={clientPayments}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </PageLayout>
  );
}
